import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { DateService, DateUnit } from "@app/services/date.service";
import { LocationInformationService } from "@app/services/location-information/location-information.service";
import { ITimelineEntry } from "@directives/_layout/timeline/timeline.component";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleDriverMapping } from "@interfaces/IVehicleDriverMapping";
import { IVehicleTrailerMapping } from "@interfaces/IVehicleTrailerMapping";
import { DriverService } from "@services/driver.service";
import { DriverAssignmentService } from "@services/driverAssignment.service";
import { GeofenceService, IGeofenceVisit } from "@services/geofence.service";
import { PlotService } from "@services/plot.service";
import { DriverTachoMode, IDriverTachoHours, TachoService } from "@services/tacho.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { IVehicleInputRecordHistory, VehicleInputService } from "@services/vehicleInput.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import * as moment from "moment";
import "./vehicleTimeline.scss";


interface IComponentBindings {
    date: Date;
    vehicle: IVehicle;
    onTimelineClicked: (args: { timelineObj: ITimelineClickData }) => void;
}

export interface ITimelineClickData {
    geofenceVisit?: IGeofenceVisit;
    driverMapping?: IVehicleDriverMapping;
    trailerMapping?: IVehicleTrailerMapping;
    inputEvent?: IVehicleInputRecordHistory;
    lat?: number;
    lng?: number;
    geofence?: any;
    driver?: IDriver;
    duration?: string;
    startTime?: moment.Moment;
    inputStart?: boolean;
    inputEnd?: boolean;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    loadingDriverData: boolean = false;
    loadingInputData: boolean = false;
    loadingTrailerData: boolean = false;
    loadingGeofenceData: boolean = false;

    date: Date;
    vehicle: IVehicle;

    trailerTableHeaders: IVeriTableHeader[] = [];
    trailerTableRows: IVeriTableRow<IVehicleTrailerMapping>[] = [];

    inputTableHeaders: IVeriTableHeader[] = [];
    inputTableRows: IVeriTableRow<IVehicleInputRecordHistory>[] = [];

    geofenceVisitTableHeaders: IVeriTableHeader[] = [];
    geofenceVisitTableRows: IVeriTableRow<IGeofenceVisit>[] = [];
    geofences: IGeofence[] = [];

    driverTableHeaders: IVeriTableHeader[] = [];
    driverTableRows: IVeriTableRow<any>[] = [];

    trailerAssociations;
    trailerEntries: ITimelineEntry[] = [];

    onTimelineClicked: (args: { timelineObj: ITimelineClickData }) => void;
    private readonly locationInformationService = new HybridServiceAccessor<LocationInformationService>("locationInformationService", this.$injector);

    static $inject = ["$injector", "$mdDialog", "$q", "vehicleService", "geofenceService", "driverService", "plotService", "driverAssignmentService", "tachoService", "veriDate", "vehicleInputService", "translationService", "veriTableService", "dateService"];

    constructor(
        private readonly $injector: angular.auto.IInjectorService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly $q: angular.IQService,
        private readonly vehicleService: VehicleService,
        private readonly geofenceService: GeofenceService,
        private readonly driverService: DriverService,
        private readonly plotService: PlotService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly tachoService: TachoService,
        private readonly veriDate: VeriDate,
        private readonly vehicleInputService: VehicleInputService,
        private readonly translationService: TranslationService,
        private readonly veriTableService: VeriTableService,
        private readonly dateService: DateService
    ) {
    }

    $onInit() {
        this.createTableHeaders();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj["vehicle"]) {
        }

        // Todo - Make this only load what is needed for the current view
        this.updateTrailerData();
        this.updateGeofenceVisits();
        this.updateDriverInformation();
        this.updateInputData();
    }

    onGeofenceClicked(row: IVeriTableRow<IGeofenceVisit>) {
        var timelineObj: ITimelineClickData = {
            geofenceVisit: row.data,
        };

        const geofenceEndTime = !!timelineObj.geofenceVisit.EndUtc ? new Date(timelineObj.geofenceVisit.EndUtc) : new Date()
                
        timelineObj.geofenceVisit.duration = this.dateService.getDiff(new Date(timelineObj.geofenceVisit.StartUtc), new Date(geofenceEndTime), DateUnit.Ms);
        timelineObj.geofenceVisit.durationFormatted = this.dateService.formatDuration(new Date(timelineObj.geofenceVisit.StartUtc), new Date(geofenceEndTime));

        var geofence = this.geofenceService.findGeofence(this.geofences, row.data.GeofenceId);

        timelineObj.lat = geofence.Latitude;
        timelineObj.lng = geofence.Longitude;

        this.onTimelineClicked({ timelineObj: timelineObj });
    }

    onInputClicked(row: IVeriTableRow<IVehicleInputRecordHistory>) {
        var timelineObj: ITimelineClickData = {
            inputEvent: row.data,
        };

        this.plotService.getPlotNearestToTime(timelineObj.inputEvent.VehicleId, moment(timelineObj.inputEvent.StartUtc))
            .then(plot => {
                if (!plot) {
                    this.$mdDialog.show(
                        (this.$mdDialog.alert() as any)
                            .clickOutsideToClose(true)
                            .title("Unable to find event location")
                            .content("No plot information is available for this event")
                            .ok("OK")
                    );

                } else {
                    timelineObj.lat = plot.Latitude;
                    timelineObj.lng = plot.Longitude;
                    timelineObj.geofence = plot.LocationInformation?.LocationDescription;
                    this.onTimelineClicked({ timelineObj: timelineObj });
                }
            });
    }

    onDriverClicked(row: IVeriTableRow<IVehicleDriverMapping>) {
        var timelineObj: ITimelineClickData = {
            driverMapping: row.cells[0].data,
            startTime: moment(row.cells[2].sortData._i)
        };

        this.driverService.getDriver(timelineObj.driverMapping.Id).then(driver => {
            timelineObj.driver = driver;
        });

        this.plotService.getPlotNearestToTime(this.vehicle.Id, timelineObj.startTime)
            .then(plot => {
                if (!plot) {
                    this.$mdDialog.show(
                        (this.$mdDialog.alert() as any)
                            .clickOutsideToClose(true)
                            .title("Unable to find event location")
                            .content("No plot information is available for this event")
                            .ok("OK")
                    );

                } else {
                    timelineObj.lat = plot.Latitude;
                    timelineObj.lng = plot.Longitude;
                    timelineObj.geofence = plot.LocationInformation?.LocationDescription;
                    this.onTimelineClicked({ timelineObj: timelineObj });
                }
            });
    }

    onTrailerClicked(row: IVeriTableRow<IVehicleTrailerMapping>) {
        var timelineObj: ITimelineClickData = {
            trailerMapping: row.data
        };

        var trailerStart = moment(timelineObj.trailerMapping.StartUtc);

        this.plotService.getPlotNearestToTime(timelineObj.trailerMapping.TrailerId, trailerStart)
            .then(plot => {
                timelineObj.lat = plot.Latitude;
                timelineObj.lng = plot.Longitude;
                timelineObj.geofence = plot.LocationInformation?.LocationDescription;
                this.onTimelineClicked({ timelineObj: timelineObj });
            });
    }

    // ------

    private updateInputData() {
        this.loadingInputData = true;

        this.vehicleInputService.getVehicleInputHistory(this.vehicle.Id, moment(this.date).startOf("d").toDate(), moment(this.date).endOf("d").toDate())
            .then(inputHistoryList => {
                return this.createInputTableRows(inputHistoryList).then(rows => {
                    this.inputTableRows = rows;
                });
            })
            .finally(() => {
                this.loadingInputData = false;
            })
    }

    private updateTrailerData() {
        this.loadingTrailerData = true;

        this.vehicleService
            .getVehicleTrailerMappingsWithVehicles(this.vehicle.Id, moment(this.date).startOf("d"), moment(this.date).endOf("d"))
            .then(associations => {

                this.trailerEntries = [];

                associations.forEach(ta => {
                    this.trailerEntries.push({
                        data: ta,
                        start: ta.StartUtc,
                        end: ta.EndUtc
                    });
                });

                this.trailerTableRows = this.createTableRows(associations);
            })
            .finally(() => {
                this.loadingTrailerData = false;
            });
    }

    private updateGeofenceVisits() {
        this.loadingGeofenceData = true;

        const geofencePromise = this.geofenceService.getGeofences();
        const visitsPromise = this.geofenceService.getGeofenceVisitsByVehicle(this.vehicle.Id, moment(this.date).startOf("d"), moment(this.date).endOf("d"))

        this.$q.all([geofencePromise, visitsPromise])
            .then(([geofences, visits]) => {
                this.geofences = geofences;
                this.geofenceVisitTableRows = this.createGeofenceVisitTableRows(visits);
            })
            .finally(() => {
                this.loadingGeofenceData = false;
            });
    }

    private updateDriverInformation() {
        this.loadingDriverData = true;

        var start = moment(this.date).startOf("d");
        var end = moment(this.date).endOf("d");

        const driverAssignmentPromise = this.driverAssignmentService.getIntersectingDriverAssignmentsForVehicle(this.vehicle.Id, start, end);
        driverAssignmentPromise.then(([drivers, mappings]) => {
            var matchingAssignments: IVehicleDriverMapping[] = [];
            mappings.forEach(mapping => {
                if (this.timeWithinDateRange(mapping.StartUtc) || this.timeWithinDateRange(mapping.EndUtc) || mapping.StartUtc.isBefore(start) && mapping.EndUtc.isAfter(end)) {
                    matchingAssignments.push(mapping);
                }
            });

            var tachoPromises: angular.IPromise<IDriverTachoHours[]>[] = [];
            matchingAssignments.forEach(mapping => {

                var tachoPromise = this.tachoService.getHistoricalTachoForDriver(mapping.DriverId, this.restrictTimeToDateRange(mapping.StartUtc).toDate(), this.restrictTimeToDateRange(mapping.EndUtc).toDate());
                tachoPromises.push(tachoPromise);
            });

            this.$q.all(tachoPromises)
                .then(driversTachoData => {
                    this.driverTableRows = this.createDriverAssignmentRows(matchingAssignments, drivers);

                    driversTachoData.forEach(driverTachoData => {
                        var driverTachoRows = this.createDriverTachoRows(driverTachoData, drivers);

                        this.driverTableRows = this.driverTableRows.concat(driverTachoRows);
                    });
                }).finally(() => {
                    this.loadingDriverData = false;
                });
        });
    }

    private createDriverTachoRows(driverTachoData: IDriverTachoHours[], drivers: IDriver[]): IVeriTableRow<any>[] {
        var tableRows: IVeriTableRow<any>[] = [];

        if (driverTachoData.length == 0) {
            return tableRows;
        }

        var previousMode: DriverTachoMode = null;
        var driver = this.driverService.findDriver(drivers, driverTachoData[0].DriverId);

        driverTachoData.forEach(tachoHour => {
            if (previousMode == null || previousMode != tachoHour.Mode) {


                const statusString: string = this.tachoService.getModeMessage(tachoHour.Mode);
                var status = `<tacho-mode [tacho-mode]="${tachoHour.Mode}"></tacho-mode> ${statusString}`;

                tableRows.push({
                    cells: this.createDriverTableCells(status, driver, moment(tachoHour.EventTime), statusString)
                });
            }

            previousMode = tachoHour.Mode;
        });

        return tableRows;
    }

    private createDriverAssignmentRows(mappings: IVehicleDriverMapping[], drivers: IDriver[]): IVeriTableRow<IVehicleDriverMapping>[] {
        var tableRows: IVeriTableRow<IVehicleDriverMapping>[] = [];

        mappings.forEach(mapping => {
            var driver = this.driverService.findDriver(drivers, mapping.DriverId);
            var assignmentSource = this.driverAssignmentService.getSourceString(mapping.AssignmentSource);
            var addedMapping = false;

            if (this.timeWithinDateRange(mapping.StartUtc)) {
                addedMapping = true;

                tableRows.push({
                    data: mapping,
                    cells: this.createDriverTableCells(this.translationService.t(`VehicleTimeline.AssignedVia`, [assignmentSource]), driver, mapping.StartUtc)
                });
            }

            if (this.timeWithinDateRange(mapping.EndUtc)) {
                addedMapping = true;

                // As assignment and deassignments happen at the same time, we need to take a second off the deassignment in order to have them appear in the correct order
                tableRows.push({
                    data: mapping,
                    cells: this.createDriverTableCells(this.translationService.t(`VehicleTimeline.Deassigned`), driver, mapping.EndUtc.add(-1, "s"))
                });
            }

            if (!addedMapping) {
                tableRows.push({
                    data: mapping,
                    cells: this.createDriverTableCells(this.translationService.t("VehicleTimeline.PreviouslyAssigned"), driver, mapping.StartUtc)
                });
            }
        });

        return tableRows;
    }

    private createDriverTableCells(status: string, driver: IDriver, time: moment.Moment, statusExportData?: string): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [];

        cells.push({ contents: `<b>${driver.Name}</b>`, data: driver, exportData: driver.Name });
        cells.push({ contents: `${status}`, exportData: statusExportData || status/*, data: driver*/ });
        cells.push({ contents: `${this.veriDate.formatDate(time)}`, sortData: time });

        return cells;
    }

    private createGeofenceVisitTableRows(visits: IGeofenceVisit[]): IVeriTableRow<IGeofenceVisit>[] {
        var tableRows: IVeriTableRow<IGeofenceVisit>[] = [];

        visits.forEach(visit => {
            tableRows.push({
                data: visit,
                cells: this.createGeofenceVisitTableCells(visit),
                //rowWarningStatus: record.activeRecords.length == 0 ? VeriTableRowStatus.OkChecked : VeriTableRowStatus.Warning
            });
        });

        return tableRows;
    }

    private createGeofenceVisitTableCells(visit: IGeofenceVisit): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [];

        var visitGeofence = this.geofenceService.findGeofence(this.geofences, visit.GeofenceId); 

        let visitDurationSortData: number;

        if (visit.EndUtc) {
            visitDurationSortData = this.dateService.getDiff(new Date(visit.StartUtc), new Date(visit.EndUtc), DateUnit.Ms);
        } else {
            visitDurationSortData = this.dateService.getDiff(new Date(visit.StartUtc), new Date(), DateUnit.Ms);
        }

        cells.push({ contents: visitGeofence.Description, data: visit, sortData: visitGeofence.Description });
        cells.push({ contents: this.dateService.formatDate(visit.StartUtc, {dateTime: true}), sortData: visit.StartUtc });

        if (visit.EndUtc) {
            cells.push({ contents: this.dateService.formatDate(visit.EndUtc, { dateTime: true }), sortData: visit.EndUtc });
            cells.push({ contents: this.dateService.formatDuration(new Date(visit.StartUtc), new Date(visit.EndUtc)), sortData: visitDurationSortData });
        }
        else {
            cells.push({ contents: this.translationService.t("VehicleTimeline.Current"), data: moment(), sortData: moment() });
            cells.push({ contents: this.dateService.formatDuration(new Date(visit.StartUtc), new Date()), sortData: visit.duration });
        }

        return cells;
    }

    private createInputTableRows(inputs: IVehicleInputRecordHistory[]): angular.IPromise<IVeriTableRow<IVehicleInputRecordHistory>[]> {
        var tableRows: IVeriTableRow<IVehicleInputRecordHistory>[] = [];

        return this.vehicleInputService.getVehicleInputs(this.vehicle.Id)
            .then(vehicleInputs => {
                inputs.forEach(input => {

                    vehicleInputs.forEach(vehicleInput => {
                        if (input.InputId == vehicleInput.Id) {
                            input.inputName = vehicleInput.name;
                            input.inputStateDescription = vehicleInput.InputStateDescription;
                        }
                    });

                    tableRows.push({
                        data: input,
                        cells: this.createInputTableCells(input),
                    });
                });

                return tableRows;
            });
    }

    private createInputTableCells(input: IVehicleInputRecordHistory): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [];

        cells.push({ contents: `${input.inputName} ${input.inputStateDescription === 0 ? 'On' : 'Open'}`, data: input, sortData: input.inputName });
        cells.push({ contents: `<location-information [plot]="{ Id: row.data.StartPlotId }"></location-information>` });
        input.EndPlotId ? cells.push({ contents: `<location-information [plot]="{ Id: row.data.EndPlotId }"></location-information>` }) : cells.push(this.veriTableService.createNACell(undefined, "--"));
        cells.push({ contents: this.veriDate.formatDate(input.StartUtc), sortData: input.StartUtc });
        cells.push({ contents: input.EndUtc ? this.veriDate.formatDate(input.EndUtc) : this.translationService.t("VehicleTimeline.Current"), sortData: input.EndUtc });
        cells.push({ contents: this.veriDate.formatDuration(moment(input.StartUtc), moment(input.EndUtc)), sortData: moment(input.EndUtc).diff(moment(input.StartUtc)) });

        return cells;
    }

    private createTableRows(associations: IVehicleTrailerMapping[]): IVeriTableRow<IVehicleTrailerMapping>[] {
        var tableRows: IVeriTableRow<IVehicleTrailerMapping>[] = [];

        associations.forEach(record => {
            tableRows.push({
                data: record,
                cells: this.createTableCells(record),
                //rowWarningStatus: record.activeRecords.length == 0 ? VeriTableRowStatus.OkChecked : VeriTableRowStatus.Warning
            });
        });

        return tableRows;
    }

    private createTableCells(record: IVehicleTrailerMapping): IVeriTableRowCell[] {
        const cells: IVeriTableRowCell[] = [];

        cells.push(this.veriTableService.createVehicleRegCell(record.vehicle));
        cells.push(this.veriTableService.createVehicleRegCell(record.trailer));
        cells.push({ contents: this.veriDate.formatDate(record.StartUtc), sortData: record.StartUtc });
        cells.push({ contents: record.EndUtc ? this.veriDate.formatDate(record.EndUtc) : "Current", sortData: record.EndUtc });
        cells.push({ contents: this.veriDate.formatDuration(record.StartUtc, record.EndUtc || moment()), sortData: moment(record.EndUtc).diff(moment(record.StartUtc)) });

        return cells;
    }

    private timeWithinDateRange(time: moment.Moment) {
        var start = moment(this.date).startOf("d");
        var end = moment(this.date).endOf("d");

        return time.isBetween(start, end);
    }

    private restrictTimeToDateRange(time: moment.Moment) {
        var start = moment(this.date).startOf("d");
        var end = moment(this.date).endOf("d");

        if (time.isBefore(start)) {
            return start;
        }
        else if (time.isAfter(end)) {
            return end;
        }

        else return time;
    }

    private createTableHeaders(): void {
        this.trailerTableHeaders = [
            { title: this.translationService.t("VehicleTimeline.TrailerTableHeaderVehicle"), sortable: true, width: 110 },
            { title: this.translationService.t("VehicleTimeline.TrailerTableHeaderTrailer"), sortable: true, width: 90 },
            { title: this.translationService.t("VehicleTimeline.TrailerTableHeaderStart"), sortable: true, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.TrailerTableHeaderEnd"), sortable: true, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.TrailerTableHeaderDuration"), sortable: true, width: 110, sortType: SortType.Number },
        ];

        this.geofenceVisitTableHeaders = [
            { title: this.translationService.t("VehicleTimeline.GeofenceTableHeaderGeofence"), sortable: true },
            { title: this.translationService.t("VehicleTimeline.GeofenceTableHeaderStart"), sortable: true, width: 140, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.GeofenceTableHeaderEnd"), sortable: true, width: 140, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.GeofenceTableHeaderDuration"), sortable: true, width: 180, sortType: SortType.Number },
        ];

        this.driverTableHeaders = [
            { title: this.translationService.t("VehicleTimeline.DriverTableHeaderDriver"), sortable: true },
            { title: this.translationService.t("VehicleTimeline.DriverTableHeaderStatus"), sortable: true },
            { title: this.translationService.t("VehicleTimeline.DriverTableHeaderDate"), sortable: true, width: 140, sortType: SortType.Date },
        ];

        this.inputTableHeaders = [
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderActiveInputs"), sortable: true, width: 180 },
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderStartLocation"), sortable: true },
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderEndLocation"), sortable: true },
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderStartTime"), sortable: true, width: 140, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderEndTime"), sortable: true, width: 140, sortType: SortType.Date },
            { title: this.translationService.t("VehicleTimeline.InputTableHeaderDuration"), sortable: true, width: 140, sortType: SortType.Number },
        ];
    }
}

export class VehicleTimelineComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<",
            date: "<",
            onTimelineClicked: "&",
            onDriverTimelineClicked: "&",
            onTrailerTimelineClicked: "&"
        };

        this.controller = ComponentController;
        this.template = require("./vehicleTimeline.html");
    }
}
